import { createContext } from 'react';
import { PortalType } from '../../../components/shared/Sidebar';
import { Plan, Role, UserAddOn } from './rules';

type Institution = {
  id: string;
  name: string;
  ministry: {
    id: string;
    name: string;
  } | null;
  type: {
    id: string;
    name: string;
  } | null;
  country: {
    id: string;
    name: string;
  } | null;
  province: {
    id: string;
    name: string;
  } | null;
  district: {
    id: string;
    name: string;
  } | null;
  districtMunicipality: {
    id: string;
    name: string;
  } | null;
  registrationOffice:
    | {
        id: string;
        name: string;
      }[]
    | null;
};

type ServiceProvider = {
  id: string;
  name: string;
};

type Funder = {
  id: string;
  name: string;
};

type Organisation = {
  id: string;
  name: string;
  type: string;
};

type UserOrganisation = {
  id: string;
  isDefault: boolean;
  role: string;
  practitionerType?: string;
  organisation: Organisation;
};

export type LoginFormValues = {
  email: string;
  password: string;
  portalType: PortalType;
};

export type User = {
  id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  addOns?: UserAddOn[];
  institution: Institution;
  permissions: string[];
  plan: Plan;
  registrationRole?: string;
  roles: Role[];
  serviceProvider?: ServiceProvider;
  funder?: Funder;
  userOrganisation: UserOrganisation;
  userOrganisations: UserOrganisation[];
  dataRole?: string;
  // createdAt?: string;
  // lastLogin?: string;
  // status?: string;
  // updatedAt?: string;
  // favoriteInsight?: string;
};

export type AuthState = {
  authenticated: boolean; // to check if authenticated or not
  acknowledgedDeclaration: boolean; // if acknowledged most recent user agreement
  user: User; // store all the user details
  accessToken: string; // accessToken of user for Auth0
  defaultRedirectRoute: string;
  error?: any;
  loading: boolean;
  organisationId: string;
  sessionId: string;
  password: string;
  hasCaseManagement: boolean;
};

export type OrganisationsState = {
  organisations: UserOrganisation[];
};

export type AuthProviderValue = AuthState & {
  login: Function;
  logout: Function;
  setActiveOrganisation: (route: string, organisationId: string, onDone?: () => void) => void;
  setAcknowledgeDeclaration: (acknowledgeDeclaration: boolean) => void;
  switchingOrganisation: boolean;
};

const user: User = {
  institution: {
    id: '',
    name: '',
    ministry: {
      id: '',
      name: '',
    },
    type: {
      id: '',
      name: '',
    },
    country: {
      id: '',
      name: '',
    },
    province: {
      id: '',
      name: '',
    },
    district: {
      id: '',
      name: '',
    },
    districtMunicipality: {
      id: '',
      name: '',
    },
    registrationOffice: [
      {
        id: '',
        name: '',
      },
    ],
  },
  permissions: [],
  plan: 'FREE_VIEWER',
  roles: ['VISITOR'],
  userOrganisation: {
    id: '',
    isDefault: false,
    role: 'VISITOR',
    organisation: {
      id: '',
      name: '',
      type: '',
    },
  },
  userOrganisations: [],
};

const defaultValues = {
  authenticated: false,
  acknowledgedDeclaration: true,
  user,
  accessToken: '',
  error: undefined,
  loading: false,
  defaultRedirectRoute: '',
  organisationId: '',
  sessionId: '',
  password: '',
  hasCaseManagement: false,
  login: (values: LoginFormValues) => Promise.resolve(),
  logout: () => {},
  setActiveOrganisation: (route: string, organisationId: string, onDone?: () => void) => {},
  setAcknowledgeDeclaration: (acknowledgeDeclaration: boolean) => {},
  switchingOrganisation: false,
};

export const AuthContext = createContext<AuthProviderValue>(defaultValues);

export const AuthProvider = AuthContext.Provider;
export const AuthConsumer = AuthContext.Consumer;
